import React, {useState, useEffect, useRef} from 'react';
import {
    Menu,
    X,
    User,
    Heart,
    Bell,
    MessageSquare,
    Settings,
    MapPin,
    ChevronDown,
    Home,
    Building,
    LogOut,
    UserPlus,
    LogIn
} from 'lucide-react';
import {useLocation, useNavigate} from "react-router-dom";
import dashboardService from "../../services/dashboardService";

const NOTIFICATION_POLL_INTERVAL_MS = 60000;

const Header = () => {
    // List of paths where header should be hidden
    const hiddenPaths = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/verify-email',
        '/onboarding',
        '/setup',
        '/welcome'
    ];

    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;

    // Check if header should be hidden on current path
    const shouldHideHeader = hiddenPaths.includes(currentPath);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);

    const userMenuRef = useRef(null);

    // Check authentication state from localStorage
    const getAuthState = () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const userData = localStorage.getItem('userData');

            if (authToken && userData) {
                return {
                    isAuthenticated: true,
                    user: JSON.parse(userData)
                };
            }
            return {
                isAuthenticated: false,
                user: null
            };
        } catch (error) {
            console.error('Error getting auth state:', error);
            return {
                isAuthenticated: false,
                user: null
            };
        }
    };

    const {isAuthenticated, user} = getAuthState();

    // Poll the real unread notification count while logged in
    useEffect(() => {
        if (!isAuthenticated) {
            setNotifications(0);
            return;
        }

        let cancelled = false;
        const fetchUnreadCount = async () => {
            try {
                const count = await dashboardService.getUnreadNotificationCount();
                if (!cancelled) setNotifications(count);
            } catch (error) {
                console.error('Failed to fetch unread notification count:', error);
            }
        };

        fetchUnreadCount();
        const intervalId = setInterval(fetchUnreadCount, NOTIFICATION_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [isAuthenticated]);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        setIsUserMenuOpen(false);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        navigate('/login');
        window.location.reload();
    };

    const handleNavigation = (path) => {
        console.log('Navigate to:', path);
        setIsMenuOpen(false);
        setIsUserMenuOpen(false);
        navigate(path);
    };

    const navigationLinks = [
        {name: 'Find Homes', href: '/listings', icon: Home},
        {name: 'Map View', href: '/map', icon: MapPin},
        {name: 'Neighborhoods', href: '/neighborhoods', icon: Building},
        {name: 'Rental Guide', href: '/rental-guide'},
        {name: 'Property Management', href: '/property-management'}
    ];

    // Dynamic user menu based on user role
    const getUserMenuItems = () => {

        if (!user) return [];

        console.log(user)
        console.log(user.role)

        if (user.role === 'manager' || user.role === 'admin') {
            return [
                {name: 'My Properties', href: '/landlord/properties', icon: Building},
                {name: 'Applications', href: '/manager/applications', icon: UserPlus},
                {name: 'Messages', href: '/manager/messages', icon: MessageSquare},
                {name: 'Analytics', href: '/manager/analytics'},
                {name: 'Profile', href: '/manager/profile', icon: User},
                {name: 'Settings', href: '/manager/settings', icon: Settings}
            ];
        } else if (user.role === 'admin') {
            return [
                {name: 'Admin Dashboard', href: '/admin', icon: Settings},
                {name: 'User Management', href: '/admin/users', icon: User},
                {name: 'Property Management', href: '/admin/properties', icon: Building},
                {name: 'System Settings', href: '/admin/settings', icon: Settings},
                {name: 'Analytics', href: '/admin/analytics'},
                {name: 'Profile', href: '/admin/profile', icon: User}
            ];
        } else {
            return [
                {name: 'Dashboard', href: '/dashboard', icon: User},
                {name: 'Saved Properties', href: '/saved-properties', icon: Heart},
                {name: 'Applications', href: '/applications', icon: UserPlus},
                {name: 'Messages', href: '/messages', icon: MessageSquare},
                {name: 'Profile', href: '/profile', icon: User}
            ];
        }
    };

    const userMenuItems = getUserMenuItems();

    // Get user display name
    const getUserDisplayName = () => {
        if (!user) return '';

        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        } else if (user.name) {
            return user.name;
        } else if (user.email) {
            return user.email.split('@')[0];
        }
        return 'User';
    };

    const userDisplayName = getUserDisplayName();

    // Don't render header if on hidden paths
    if (shouldHideHeader) {
        return null;
    }

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled ? 'bg-karibu-100 shadow-lg' : 'bg-karibu-100/95 backdrop-blur-md'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <button
                            onClick={() => handleNavigation('/')}
                            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                            aria-label="Go to homepage"
                        >
                            <div
                                className="w-8 h-8 bg-highland-500 rounded-lg flex items-center justify-center">
                                <Home className="w-5 h-5 text-karibu-100"/>
                            </div>
                            <span className="text-xl font-display font-bold text-soil-900">Rent Utopia</span>
                        </button>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        {navigationLinks.map((link) => {
                            const Icon = link.icon;
                            const isActive = currentPath === link.href || (link.href !== '/' && currentPath.startsWith(link.href));
                            return (
                                <button
                                    key={link.name}
                                    onClick={() => handleNavigation(link.href)}
                                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                                        isActive
                                            ? 'text-highland-500 bg-highland-50'
                                            : 'text-soil-600 hover:text-highland-500 hover:bg-highland-50'
                                    }`}
                                    aria-label={`Navigate to ${link.name}`}
                                >
                                    {Icon && <Icon className="w-4 h-4"/>}
                                    <span>{link.name}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* User Actions */}
                    <div className="flex items-center space-x-4">
                        {isAuthenticated ? (
                            <>
                                {/* Notifications */}
                                <button
                                    onClick={() => handleNavigation('/notifications')}
                                    className="relative p-2 text-soil-600 hover:text-highland-500 transition-colors duration-200"
                                    aria-label="View notifications"
                                >
                                    <Bell className="w-5 h-5"/>
                                    {notifications > 0 && (
                                        <span
                                            className="absolute -top-1 -right-1 bg-murram-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            {notifications > 9 ? '9+' : notifications}
                                        </span>
                                    )}
                                </button>

                                {/* Messages */}
                                <button
                                    onClick={() => handleNavigation('/messages')}
                                    className="hidden sm:block p-2 text-soil-600 hover:text-highland-500 transition-colors duration-200"
                                    aria-label="View messages"
                                >
                                    <MessageSquare className="w-5 h-5"/>
                                </button>

                                {/* User Menu */}
                                <div className="relative" ref={userMenuRef}>
                                    <button
                                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-soil-50 transition-colors duration-200"
                                        aria-label="Open user menu"
                                    >
                                        <div
                                            className="w-8 h-8 bg-highland-500 rounded-full flex items-center justify-center">
                                            {user?.avatar ? (
                                                <img src={user.avatar} alt={userDisplayName}
                                                     className="w-8 h-8 rounded-full object-cover"/>
                                            ) : (
                                                <User className="w-4 h-4 text-white"/>
                                            )}
                                        </div>
                                        <span
                                            className="hidden sm:block text-sm font-medium text-soil-600 max-w-24 truncate">
                                            {userDisplayName.split(' ')[0]}
                                        </span>
                                        <ChevronDown className="w-4 h-4 text-soil-400"/>
                                    </button>

                                    {/* User Dropdown */}
                                    {isUserMenuOpen && (
                                        <div
                                            className="absolute right-0 mt-2 w-64 bg-karibu-100 rounded-lg shadow-lg border border-soil-100 py-2 z-50">
                                            <div className="px-4 py-3 border-b border-soil-100">
                                                <p className="text-sm font-medium text-soil-900 truncate">{userDisplayName}</p>
                                                <p className="text-sm text-soil-400 truncate">{user?.email}</p>
                                                {user?.userType && (
                                                    <p className="text-xs text-highland-500 font-medium capitalize mt-1">
                                                        {user.userType}
                                                    </p>
                                                )}
                                            </div>
                                            {userMenuItems.map((item) => {
                                                const Icon = item.icon;
                                                const isActive = currentPath === item.href || (item.href !== '/' && currentPath.startsWith(item.href));
                                                return (
                                                    <button
                                                        key={item.name}
                                                        onClick={() => handleNavigation(item.href)}
                                                        className={`flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors duration-200 text-left ${
                                                            isActive
                                                                ? 'text-highland-500 bg-highland-50'
                                                                : 'text-soil-600 hover:bg-soil-50'
                                                        }`}
                                                        aria-label={`Navigate to ${item.name}`}
                                                    >
                                                        {Icon && <Icon className="w-4 h-4"/>}
                                                        <span>{item.name}</span>
                                                    </button>
                                                );
                                            })}
                                            <div className="border-t border-soil-100 mt-2 pt-2">
                                                <button
                                                    onClick={handleLogout}
                                                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-murram-500 hover:bg-murram-100/40 transition-colors duration-200"
                                                    aria-label="Sign out"
                                                >
                                                    <LogOut className="w-4 h-4"/>
                                                    <span>Sign Out</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            // Only show Sign In/Sign Up on desktop for non-authenticated users
                            <div className="hidden lg:flex items-center space-x-3">
                                <button
                                    onClick={() => handleNavigation('/login')}
                                    className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-soil-600 hover:text-highland-500 transition-colors duration-200"
                                    aria-label="Sign in"
                                >
                                    <LogIn className="w-4 h-4"/>
                                    <span>Sign In</span>
                                </button>
                                <button
                                    onClick={() => handleNavigation('/register')}
                                    className="flex items-center space-x-1 px-4 py-2 bg-highland-500 text-white text-sm font-medium rounded-md hover:bg-highland-600 transition-colors duration-200"
                                    aria-label="Sign up"
                                >
                                    <UserPlus className="w-4 h-4"/>
                                    <span>Sign Up</span>
                                </button>
                            </div>
                        )}

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="lg:hidden p-2 text-soil-600 hover:text-highland-500 transition-colors duration-200"
                            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            {isMenuOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div
                        className="lg:hidden absolute top-16 left-0 right-0 bg-karibu-100 shadow-lg border-t border-soil-100">
                        <div className="px-4 py-4 space-y-4">
                            {/* Mobile Navigation Links */}
                            <nav className="space-y-2">
                                {navigationLinks.map((link) => {
                                    const Icon = link.icon;
                                    const isActive = currentPath === link.href || (link.href !== '/' && currentPath.startsWith(link.href));
                                    return (
                                        <button
                                            key={link.name}
                                            onClick={() => handleNavigation(link.href)}
                                            className={`flex items-center space-x-3 w-full px-3 py-3 rounded-md text-left transition-colors duration-200 ${
                                                isActive
                                                    ? 'text-highland-500 bg-highland-50'
                                                    : 'text-soil-600 hover:text-highland-500 hover:bg-soil-50'
                                            }`}
                                            aria-label={`Navigate to ${link.name}`}
                                        >
                                            {Icon && <Icon className="w-5 h-5"/>}
                                            <span className="font-medium">{link.name}</span>
                                        </button>
                                    );
                                })}
                            </nav>

                            {/* Mobile User Section */}
                            {isAuthenticated ? (
                                <div className="pt-4 border-t border-soil-100 space-y-2">
                                    <div className="px-3 py-2 text-sm text-soil-400">
                                        Signed in as <span
                                        className="font-medium text-soil-900">{userDisplayName}</span>
                                    </div>
                                    <button
                                        onClick={() => handleNavigation('/notifications')}
                                        className={`flex items-center space-x-3 w-full px-3 py-3 rounded-md transition-colors duration-200 ${
                                            currentPath === '/notifications'
                                                ? 'text-highland-500 bg-highland-50'
                                                : 'text-soil-600 hover:text-highland-500 hover:bg-soil-50'
                                        }`}
                                        aria-label="View notifications"
                                    >
                                        <Bell className="w-5 h-5"/>
                                        <span className="font-medium">Notifications</span>
                                        {notifications > 0 && (
                                            <span
                                                className="ml-auto bg-murram-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                {notifications > 9 ? '9+' : notifications}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleNavigation('/messages')}
                                        className={`flex items-center space-x-3 w-full px-3 py-3 rounded-md transition-colors duration-200 ${
                                            currentPath === '/messages'
                                                ? 'text-highland-500 bg-highland-50'
                                                : 'text-soil-600 hover:text-highland-500 hover:bg-soil-50'
                                        }`}
                                        aria-label="View messages"
                                    >
                                        <MessageSquare className="w-5 h-5"/>
                                        <span className="font-medium">Messages</span>
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center space-x-3 w-full px-3 py-3 rounded-md text-murram-500 hover:bg-murram-100/40 transition-colors duration-200"
                                        aria-label="Sign out"
                                    >
                                        <LogOut className="w-5 h-5"/>
                                        <span className="font-medium">Sign Out</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="pt-4 border-t border-soil-100 space-y-2">
                                    <button
                                        onClick={() => handleNavigation('/login')}
                                        className={`flex items-center space-x-3 w-full px-3 py-3 rounded-md transition-colors duration-200 ${
                                            currentPath === '/login'
                                                ? 'text-highland-500 bg-highland-50'
                                                : 'text-soil-600 hover:text-highland-500 hover:bg-soil-50'
                                        }`}
                                        aria-label="Sign in"
                                    >
                                        <LogIn className="w-5 h-5"/>
                                        <span className="font-medium">Sign In</span>
                                    </button>
                                    <button
                                        onClick={() => handleNavigation('/register')}
                                        className={`flex items-center space-x-3 w-full px-3 py-3 rounded-md transition-colors duration-200 ${
                                            currentPath === '/register'
                                                ? 'text-highland-500 bg-highland-50'
                                                : 'bg-highland-500 text-white hover:bg-highland-600'
                                        }`}
                                        aria-label="Sign up"
                                    >
                                        <UserPlus className="w-5 h-5"/>
                                        <span className="font-medium">Sign Up</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;